import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Box, Button, Collapse, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import costIcon from "@iconify/icons-mdi/cash";
import projectIcon from "@iconify/icons-mdi/chart-gantt";
import timerOutlineIcon from "@iconify/icons-mdi/timer-outline";
import timerPlusOutlineIcon from "@iconify/icons-mdi/timer-plus-outline";
import clientIcon from "@iconify/icons-mdi/user-tie";
import { Icon, InlineIcon } from "@iconify/react";
import { useFormik } from "formik";
import {
  MyTimesheetClientSelector,
  MyTimesheetCostSelector,
  MyTimesheetProjectSelector,
  MyTimesheetRequestDialog,
} from "my-timesheet/components";
import { useMyTimesheetTimesheetList } from "my-timesheet/hooks";
import { checkOverlappingEvents } from "my-timesheet/utils";
import { OcCard, OcConfirmationDialog } from "oc/components";
import { useOcCurrentUserState } from "oc/hooks";
import * as Yup from "yup";

type MyTimesheetTodaysCardProps = {
  updateMyRequest: (args: any) => Promise<any>;
  openedItem: any;
  refetchMysheetTimesheets: () => void;
  removeMyRequest: (id: string) => Promise<void> | void;
  events: any[];
  isMobile: boolean;
};

export const MyTimesheetTodaysCard = ({
  updateMyRequest,
  openedItem,
  refetchMysheetTimesheets,
  removeMyRequest,
  events,
  isMobile,
}: MyTimesheetTodaysCardProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { contractCode } = useOcCurrentUserState();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const periodStart = moment(new Date()).startOf("month").toDate();
  const periodEnd = moment(new Date()).endOf("month").toDate();

  const { createMyRequest } = useMyTimesheetTimesheetList({
    contractId: contractCode,
    periodStart: periodStart,
    periodEnd: periodEnd,
    role: "user",
  });

  const [startRecording, setStartRecording] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [requestData, setRequestData] = useState<any>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [, setPossibleEndTime] = useState<string | null>(null);

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      projectId: "",
      clientId: "",
      costId: "",
      endTime: "",
      startTime: "",
    },
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: Yup.object({
      name: Yup.string().required("OC_NAME_REQUIRED"),
      description: Yup.string().max(255, "MY_TIMESHEET_NAME_TOO_LONG"),
      projectId: Yup.string(),
      clientId: Yup.string(),
      costId: Yup.string(),
    }),
    onSubmit: async (values) => {
      const eventValues = {
        ...values,
        startTime: values.startTime ? new Date(values.startTime) : undefined,
        endTime: values.endTime ? new Date(values.endTime) : undefined,
      };
      const hasOverlap = checkOverlappingEvents(events, eventValues);

      if (hasOverlap) {
        setErrorMessage(t("OC_OVERLAPPING_TIME_PERIODS"));
        return;
      }
      await createMyRequest({
        name: values.name,
        description: values.description || null,
        startTime: values.startTime
          ? new Date(values.startTime).toISOString()
          : new Date().toISOString(),
        endTime: values.endTime
          ? new Date(values.endTime).toISOString()
          : null,
        projectId: values.projectId || null,
        clientId: values.clientId || null,
        costId: values.costId || null,
      });
      setStartRecording(false);
      setShowValues(true);
      refetchMysheetTimesheets();
      localStorage.setItem("showValues", JSON.stringify(true));
    },
  });

  const [showValues, setShowValues] = useState(() => {
    const item = localStorage.getItem("showValues");
    return item ? JSON.parse(item) : false;
  });

  useEffect(() => {
    if (openedItem) {
      setShowValues(true);
      localStorage.setItem("showValues", JSON.stringify(true));
    } else {
      setShowValues(false);
      localStorage.removeItem("showValues");
    }

    if (openedItem?.startTime && !openDialog) {
      const startTime = new Date(openedItem.startTime).getTime();
      const intervalId = setInterval(() => {
        const now = Date.now();
        setTimeElapsed(Math?.floor((now - startTime) / 1000));
      }, 1000);
      return () => clearInterval(intervalId);
    }
  }, [openedItem, openDialog]);

  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleNewRequest = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (openedItem) return;
    setStartRecording(true);
  };
  const handleStopTimer = () => {
    const currentEnd = new Date().toISOString();
    setPossibleEndTime(currentEnd);
    if (openedItem) {
      setRequestData({
        ...openedItem,
        endTime: currentEnd,
      });
    }
    setOpenDialog(true);
  };

  const handleCancel = () => {
    setStartRecording(false);
    formik.resetForm();
    setShowValues(false);
    localStorage.removeItem("showValues");
    setErrorMessage(null);
  };

  const handleRemoveRequest = () => {
    const _id = openedItem?._id;
    if (!_id) return;
    removeMyRequest(String(_id));
    setStartRecording(false);
    formik.resetForm();
    setShowValues(false);
    setShowConfirmDialog(false);
    localStorage.removeItem("showValues");
  };

  useEffect(() => {
    if (requestData) {
      formik.setValues((prev) => ({ ...prev, ...requestData }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestData]);

  return (
    <OcCard
      label={t("MY_TIMESHEET_ACTIVITY_RECORD")}
      height="-webkit-fill-available"
    >
      {!startRecording && !showValues && (
        <Box display="flex" justifyContent="center" padding="16px">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              width: "160px",
              height: "160px",
              borderRadius: "80px",
              backgroundColor: openedItem
                ? theme.palette.gray30.main
                : theme.palette.primary.main,
              cursor: "pointer",
            }}
            onClick={handleNewRequest}
          >
            <Box
              id="clock-in-button"
              display="flex"
              flexDirection="column"
              alignItems="center"
              color={
                openedItem
                  ? theme.palette.gray40.main
                  : theme.palette.white.main
              }
            >
              <Icon icon={timerPlusOutlineIcon} height="36px" />
              <Box height="16px" />
              <Typography
                variant="body1"
                color={openedItem ? "gray40.main" : "white.main"}
              >
                {t("MY_TIMESHEET_CLOCK_IN")}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
      <Collapse in={startRecording}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <TextField
            id="name"
            name="name"
            label={t("OC_NAME")}
            variant="outlined"
            style={{ width: isMobile ? "100%" : "568px" }}
            value={formik.values.name}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={
              formik.touched.name && formik.errors.name
                ? t(formik.errors.name)
                : " "
            }
            onChange={formik.handleChange}
            onFocus={() => formik.setFieldTouched("name", false)}
          />
          <Box height="15px" />
          <MyTimesheetProjectSelector formik={formik} />
          <MyTimesheetClientSelector formik={formik} />
          <MyTimesheetCostSelector formik={formik} />
          <Box height="17px" />
          <TextField
            id="description"
            label={t("OC_NOTE")}
            name="description"
            multiline
            minRows={2.3}
            variant="outlined"
            fullWidth
            style={{ width: isMobile ? "100%" : "568px", height: "auto" }}
            value={formik.values.description || ""}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={
              !formik.values.description
                ? t("OC_OPTIONAL")
                : `${formik.values.description?.length}/255`
            }
            onChange={formik.handleChange}
            onFocus={() => formik.setFieldTouched("description", false)}
            FormHelperTextProps={
              !formik.values.description
                ? { style: { textAlign: "left" } }
                : { style: { textAlign: "right" } }
            }
          />
        </Box>
        <Box height="16px" />
        <Box
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: theme.palette.gray40.main,
          }}
        />
        <Box height="16px" />
        <Collapse in={!!errorMessage}>
          {errorMessage && (
            <Box marginBottom="16px">
              <Typography variant="body2" color={theme.palette.primary.main}>
                {errorMessage}
              </Typography>
            </Box>
          )}
        </Collapse>
        <Box
          display="flex"
          width="100%"
          justifyContent="space-between"
          flexDirection={isMobile ? "column-reverse" : "row"}
        >
          <Button
            id="cancel-button"
            variant="text"
            color="secondary"
            fullWidth
            style={{ marginRight: "16px" }}
            onClick={handleCancel}
          >
            {t("OC_CANCEL")}
          </Button>
          {isMobile && <Box height="16px" />}
          <Button
            id="save-button"
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
            onClick={(e) => {
              e.preventDefault();
              formik.handleSubmit();
            }}
            disabled={!formik.values.name}
          >
            {t("MY_TIMESHEET_CLOCK_IN")}
          </Button>
        </Box>
      </Collapse>
      <Collapse in={showValues}>
        <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
          <Box
            width="100%"
            height="236px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
              backgroundColor: theme.palette.gray30.main,
              borderRadius: "4px",
              marginRight: "16px",
            }}
          >
            <Box
              style={{
                fontSize: "64px",
                color: theme.palette.primary.main,
              }}
            >
              {formatTime(timeElapsed)}
            </Box>
          </Box>
          {isMobile && <Box height="16px" />}
          <Box>
            <Box
              width="auto"
              style={{
                padding: "8px 16px",
                backgroundColor: "#E3E5E5",
                borderRadius: "4px",
              }}
            >
              <Box display="flex" flexDirection="row">
                <InlineIcon
                  icon={timerOutlineIcon}
                  width="24px"
                  style={{
                    marginRight: "8px",
                    color: "#cc0033",
                  }}
                />
                <Typography variant="body1" color="primary">
                  {openedItem?.name}
                </Typography>
              </Box>
              {openedItem?.projectName && (
                <Box display="flex" flexDirection="row">
                  <InlineIcon
                    icon={projectIcon}
                    width="24px"
                    style={{
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="body1">
                    {openedItem?.projectName}
                  </Typography>
                </Box>
              )}
              {openedItem?.clientName && (
                <Box display="flex" flexDirection="row">
                  <InlineIcon
                    icon={clientIcon}
                    width="24px"
                    style={{
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="body1">
                    {openedItem?.clientName}
                  </Typography>
                </Box>
              )}
              {openedItem?.costName && (
                <Box display="flex" flexDirection="row">
                  <InlineIcon
                    icon={costIcon}
                    width="24px"
                    style={{
                      marginRight: "8px",
                    }}
                  />
                  <Typography variant="body1">
                    {openedItem?.costName}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box height="16px" />
            <TextField
              id="outlined-multiline-static"
              label={t("OC_NOTE")}
              name="description"
              multiline
              minRows={3.7}
              variant="outlined"
              disabled={true}
              style={{ width: isMobile ? "100%" : "568px", height: "auto" }}
              value={openedItem?.description || ""}
              helperText={
                !openedItem?.description
                  ? t("OC_OPTIONAL")
                  : `${openedItem?.description?.length}/255`
              }
              FormHelperTextProps={
                !openedItem?.description
                  ? { style: { textAlign: "left" } }
                  : { style: { textAlign: "right" } }
              }
              InputLabelProps={{
                shrink: !openedItem?.description ? false : true,
              }}
            />
          </Box>
        </Box>
        <Box height="32px" />
        <Box
          style={{
            width: "100%",
            height: "1px",
            backgroundColor: theme.palette.gray40.main,
          }}
        />
        <Box height="16px" />
        <Box
          display="flex"
          flexDirection={isMobile ? "column-reverse" : "row"}
          justifyContent="space-between"
        >
          <Button
            id="cancel-start-of-work-button"
            variant="text"
            color="secondary"
            onClick={() => setShowConfirmDialog(true)}
            style={{
              width: isMobile ? "auto" : "calc(50% - 8px)",
              color: theme.palette.primary.main,
            }}
          >
            {t("OC_REMOVE_REQUEST")}
          </Button>
          {isMobile && <Box height="16px" />}
          <Button
            id="start-of-work-button"
            variant="contained"
            color="primary"
            style={{
              width: isMobile ? "auto" : "calc(50% - 8px)",
              boxShadow: "unset",
            }}
            onClick={handleStopTimer}
          >
            {t("MY_TIMESHEET_END_OF_WORK")}
          </Button>
        </Box>
      </Collapse>
      <MyTimesheetRequestDialog
        requestData={requestData}
        openDialog={openDialog}
        updateMyRequest={async ({ variables }) => {
          await updateMyRequest(variables);
          return Promise.resolve();
        }} onClose={() => setOpenDialog(false)}
        setStartRecording={setStartRecording}
        setShowValues={setShowValues}
        formik={formik}
        events={events}
        isMobile={isMobile}
      />
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleRemoveRequest}
        onCancel={() => setShowConfirmDialog(false)}
        title={t("MY_TIMESHEET_REVOKE_REQUEST")}
        texts={[t("OC_REVOKE_REQUEST_CONFIRM")]}
        okButtonText={t("OC_REMOVE_REQUEST")}
        cancelButtonText={t("OC_CANCEL")}
      />
    </OcCard>
  );
};

export default MyTimesheetTodaysCard;
